/**
 * IndexedDB Storage Layer for MedTranslate
 *
 * Stores documents, translations, and chat history locally
 * for offline access.
 */

const DB_NAME = 'medtranslate';
const DB_VERSION = 1;

let db = null;

/**
 * Initialize the IndexedDB database
 */
export async function initDB() {
  if (db) return db;

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);

    request.onsuccess = () => {
      db = request.result;
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;

      // Documents store
      if (!database.objectStoreNames.contains('documents')) {
        const docStore = database.createObjectStore('documents', { keyPath: 'id' });
        docStore.createIndex('createdAt', 'createdAt', { unique: false });
        docStore.createIndex('synced', 'synced', { unique: false });
      }

      // Translations store
      if (!database.objectStoreNames.contains('translations')) {
        const transStore = database.createObjectStore('translations', { keyPath: 'id' });
        transStore.createIndex('documentId', 'documentId', { unique: false });
        transStore.createIndex('language', 'language', { unique: false });
        transStore.createIndex('synced', 'synced', { unique: false });
      }

      // Chat messages store
      if (!database.objectStoreNames.contains('chatMessages')) {
        const chatStore = database.createObjectStore('chatMessages', { keyPath: 'id', autoIncrement: true });
        chatStore.createIndex('documentId', 'documentId', { unique: false });
        chatStore.createIndex('createdAt', 'createdAt', { unique: false });
        chatStore.createIndex('synced', 'synced', { unique: false });
      }

      // Sync queue store - for operations that need to be synced when online
      if (!database.objectStoreNames.contains('syncQueue')) {
        const syncStore = database.createObjectStore('syncQueue', { keyPath: 'id', autoIncrement: true });
        syncStore.createIndex('type', 'type', { unique: false });
        syncStore.createIndex('createdAt', 'createdAt', { unique: false });
      }

      // App state store - for misc app data
      if (!database.objectStoreNames.contains('appState')) {
        database.createObjectStore('appState', { keyPath: 'key' });
      }
    };
  });
}

/**
 * Generic function to add an item to a store
 */
async function addItem(storeName, item) {
  const database = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.put(item);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Generic function to get an item from a store
 */
async function getItem(storeName, key) {
  const database = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.get(key);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Generic function to get all items from a store
 */
async function getAllItems(storeName) {
  const database = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Generic function to delete an item from a store
 */
async function deleteItem(storeName, key) {
  const database = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.delete(key);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get items by index
 */
async function getByIndex(storeName, indexName, value) {
  const database = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const index = store.index(indexName);
    const request = index.getAll(value);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// ============ Document Operations ============

export async function saveDocument(document) {
  const doc = {
    ...document,
    cachedAt: new Date().toISOString(),
    synced: true,
  };
  return addItem('documents', doc);
}

export async function getDocument(id) {
  return getItem('documents', id);
}

export async function getAllDocuments() {
  const docs = await getAllItems('documents');
  return docs.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
}

export async function deleteDocument(id) {
  // Also delete related translations and chat messages
  const translations = await getTranslationsByDocument(id);
  const messages = await getChatMessages(id);

  for (const t of translations) {
    await deleteItem('translations', t.id);
  }
  for (const m of messages) {
    await deleteItem('chatMessages', m.id);
  }

  return deleteItem('documents', id);
}

// ============ Translation Operations ============

export async function saveTranslation(translation) {
  const trans = {
    ...translation,
    id: translation.id || `${translation.documentId}_${translation.language}`,
    cachedAt: new Date().toISOString(),
    synced: true,
  };
  return addItem('translations', trans);
}

export async function getTranslation(documentId, language) {
  const id = `${documentId}_${language}`;
  return getItem('translations', id);
}

export async function getTranslationsByDocument(documentId) {
  return getByIndex('translations', 'documentId', documentId);
}

// ============ Chat Operations ============

export async function saveChatMessage(message) {
  const msg = {
    ...message,
    cachedAt: new Date().toISOString(),
    synced: message.synced !== undefined ? message.synced : true,
  };
  return addItem('chatMessages', msg);
}

export async function getChatMessages(documentId) {
  const messages = await getByIndex('chatMessages', 'documentId', documentId);
  return messages.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
}

// ============ Sync Queue Operations ============

export async function addToSyncQueue(operation) {
  const item = {
    ...operation,
    createdAt: new Date().toISOString(),
    status: 'pending',
  };
  return addItem('syncQueue', item);
}

export async function getSyncQueue() {
  return getAllItems('syncQueue');
}

export async function removeFromSyncQueue(id) {
  return deleteItem('syncQueue', id);
}

export async function clearSyncQueue() {
  const items = await getSyncQueue();
  for (const item of items) {
    await deleteItem('syncQueue', item.id);
  }
}

// ============ App State Operations ============

export async function setAppState(key, value) {
  return addItem('appState', { key, value, updatedAt: new Date().toISOString() });
}

export async function getAppState(key) {
  const item = await getItem('appState', key);
  return item ? item.value : null;
}

// ============ Utility Functions ============

export async function getStorageStats() {
  const documents = await getAllDocuments();
  const translations = await getAllItems('translations');
  const messages = await getAllItems('chatMessages');
  const syncQueue = await getSyncQueue();

  return {
    documents: documents.length,
    translations: translations.length,
    chatMessages: messages.length,
    pendingSync: syncQueue.length,
  };
}

export async function clearAllData() {
  const database = await initDB();
  const storeNames = ['documents', 'translations', 'chatMessages', 'syncQueue', 'appState'];

  for (const storeName of storeNames) {
    await new Promise((resolve, reject) => {
      const transaction = database.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.clear();
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }
}

// Initialize DB on module load
initDB().catch(console.error);
